import { api } from './client'

export interface StatusCountStat {
  status: string
  count: number
}

// TASK-0191 / REQ-069-002: "Lead Persi per Motivo" — same { key, count } shape as
// StatusCountStat above, but the JSON key is `reason` (StatsController.Get's leadsByLostReason),
// not `status`, so a dedicated interface is used rather than reusing StatusCountStat verbatim —
// reusing it here would silently mistype the field as `.status` when the wire payload actually
// carries `.reason`.
export interface ReasonCountStat {
  reason: string
  count: number
}

// TASK-0200 / REQ-058-014: `value`/`amount`/`contracts.totalValue` below are OPTIONAL, not
// nullable — StatsController.Get omits the key entirely (never sends `0`/`null`) for a caller whose
// UserCategory lacks CanSeeRealAmounts, the same "absent key, not a zeroed value" convention
// ContestsController.Get and StatsController.Compare's `commissionsTotal` already follow. Rendering
// code must therefore branch on `=== undefined` and show a non-numeric placeholder — NEVER
// `formatEur(value ?? 0)`, which would display a fabricated "€ 0" indistinguishable from a real
// zero. Note this is a DIFFERENT gate from ADR-008 row visibility: a denied caller still receives
// every row/count/name, only the money is missing.
export interface ProductTypeStat {
  category: string
  count: number
  value?: number
}

export interface OperatorStat {
  operatorName: string
  count: number
  value?: number
  confirmedCount: number
}

export interface RecentContractStat {
  id: number
  referenceCode: string
  customerName: string | null
  operatorName: string
  amount?: number
  status: string
}

export interface UpcomingAppointmentStat {
  id: number
  title: string
  startAt: string
  status: string
  contactName: string | null
}

export interface ContestStat {
  id: number
  name: string
  contestType: string
  prize: string | null
  startDate: string
  endDate: string
}

// TASK-0030: currentValue/percentage are deliberately absent — Target.UpdateProgress has zero
// callers in the live application, so those two fields are never real data (see StatsController's
// remarks). Do not add them here as optional/zero-defaulted fields.
export interface TargetStat {
  id: number
  name: string
  targetType: string
  targetValue: number
  status: string
  startDate: string
  endDate: string
}

export interface CrmStats {
  leads: { total: number; new: number; thisMonth: number }
  customers: { total: number; person: number; company: number }
  // TASK-0200: totalValue optional — absent for a caller without CanSeeRealAmounts (see the note
  // above ProductTypeStat). total/active/thisMonth are counts, always present.
  // REQ-014-002: cancelled/rejected/activationRateOnContracts are the spec-section-14 "Dashboard
  // agente" production indicators. Like total/active/thisMonth they are counts and a ratio, never
  // money, so they are NOT optional — StatsController.Get emits them in BOTH branches of its
  // CanSeeRealAmounts if/else. `activationRateOnContracts` is a 0..1 fraction rounded to 4
  // decimals, or `null` when the period holds no contracts at all (an UNDEFINED rate, not 0%) —
  // exactly the same convention as funnels.ts's identically-named field, whose disambiguated name
  // this one deliberately reuses (KPI_DICTIONARY.md section 4 / DEC-008: never a bare
  // "activationRate", which would collide with a different-denominator KPI of the same name).
  // Render null as an em-dash via a formatPct-style helper, never as "0%".
  // REQ-014-003: expectedValue ("fatturato presunto")/collectedValue ("fatturato incassato") join
  // totalValue ("fatturato maturato/accrued") as two more optional money keys, same absent-not-
  // zeroed convention — StatsController.Get omits all three together for a caller without
  // CanSeeRealAmounts. collectedValue carries a real caveat (see StatsController.Get's own remarks):
  // Contract.CollectedAmount has no application write path today, so it reads as a static, migrated
  // figure — render it plainly, but do not imply it updates as payments are recorded.
  contracts: {
    total: number
    active: number
    cancelled: number
    rejected: number
    activationRateOnContracts: number | null
    thisMonth: number
    totalValue?: number
    expectedValue?: number
    collectedValue?: number
  }
  operations: { pendingTasks: number; appointmentsToday: number }
  contractsByStatus: StatusCountStat[]
  leadsByLostReason: ReasonCountStat[]
  productsByType: ProductTypeStat[]
  contractsByOperator: OperatorStat[]
  recentContracts: RecentContractStat[]
  upcomingAppointments: UpcomingAppointmentStat[]
  activeContests: ContestStat[]
  activeTargets: TargetStat[]
  // TASK-0242: totalCommissions was already optional here in spirit (StatsController.Get omits it
  // for a caller without CanSeeTotalCommissions) but had never been added to this interface, since
  // no frontend code consumed it yet — see git history. REQ-014-003 adds its two siblings
  // (totalPresumedCommissions = raw pre-ledger CommissionAmount sum, "provvigioni presunte";
  // totalPaidCommissions = AgentCommission.PaidAmount sum, "provvigioni pagate") under the exact
  // same optional-key, same-gate convention, and types totalCommissions itself here for the first
  // time since Dashboard.tsx now reads all three together.
  totalCommissions?: number
  totalPresumedCommissions?: number
  totalPaidCommissions?: number
}

// TASK-0070: dateFrom/dateTo are optional bare "YYYY-MM-DD" strings — exactly what an
// <input type="date"> produces, and exactly the shape StatsController.Get's new query
// parameters expect (same convention already established for ContractsController.List's
// dateFrom/dateTo, TASK-0064). Omitted/empty means "no filter, all-time" — unchanged
// pre-TASK-0070 behavior.
export function fetchStats(dateFrom?: string, dateTo?: string): Promise<CrmStats> {
  const params = new URLSearchParams()
  if (dateFrom) params.set('dateFrom', dateFrom)
  if (dateTo) params.set('dateTo', dateTo)
  const qs = params.toString()
  return api<CrmStats>(`/api/stats${qs ? `?${qs}` : ''}`)
}

// TASK-0146 / REQ-015-001 / REQ-015-002: "Confronto tra periodi" — GET /api/stats/compare's
// response shape. valueA/valueB/absoluteDifference/percentageDifference are computed
// server-side (see StatsController.Compare's remarks for why); percentageDifference is `null`
// (never a fake 0%/Infinity) when valueA is 0.
export interface ComparisonPeriodStat {
  from: string | null
  to: string | null
  leads: { total: number }
  customers: { total: number }
  // TASK-0202 / REQ-058-014: totalValue optional — StatsController.Compare omits the key entirely
  // (never `0`/`null`) for a caller without CanSeeRealAmounts, exactly as it already omits the
  // `contractsTotalValue`/`commissionsTotal` entries of `metrics`, and exactly as Get does for
  // CrmStats.contracts.totalValue above. `total` is a count and is always present.
  contracts: { total: number; totalValue?: number }
}

export interface ComparisonMetricStat {
  metric: string
  label: string
  valueA: number
  valueB: number
  absoluteDifference: number
  percentageDifference: number | null
}

export interface CrmStatsComparison {
  periodA: ComparisonPeriodStat
  periodB: ComparisonPeriodStat
  metrics: ComparisonMetricStat[]
}

// TASK-0269 (F-015) / REQ-036-001 / REQ-036-002: GET /api/stats/backoffice's response shape — a
// SEPARATE endpoint (BackofficeDashboardController), not a field added to CrmStats above. Every
// field here is a plain count, "solo conteggi" per the card. `automationErrors` is the one
// exception to "always present": BackofficeDashboardController omits the key entirely (never `0`)
// for a caller who does not separately pass PolicyNames.Admin — same "absent key, not a zeroed
// value" convention as CrmStats.contracts.totalValue above — so it is optional here too, and
// BackofficeDashboardPage.tsx must branch on `=== undefined`, never default it to 0.
//
// Review round (P1-2, coordinator decision): `pendingApproval` was "ApprovazioneRichiesta pratiche
// + lotti InAttesaApprovazione" summed into one number — a tile reading e.g. "7" that opens a list
// of 3 pratiche. Split into `pendingApproval` (pratiche only) and `batchesPendingApproval` (lotti
// only, and only those actually SUBMITTED — Batch.SubmittedAt != null, not just Status ==
// InAttesaApprovazione, which is the entity's default at creation — see
// BackofficeDashboardController's remarks for the full P1-1/P1-2 reasoning). Eight riquadri now,
// not seven — a deliberate, coordinator-authorized deviation from the card.
//
// Review round (P1-3): `pendingVerification` now counts ContractStatus.DaVerificare ONLY — see
// BackofficeDashboardController's remarks for why InviataAlBackoffice was dropped.
export interface BackofficeDashboardStats {
  pendingVerification: number
  integrationRequested: number
  readyForDispatch: number
  pendingApproval: number
  batchesPendingApproval: number
  automationErrors?: number
  operatorDrafts: number
  staleOver48h: number
}

export function fetchBackofficeDashboard(): Promise<BackofficeDashboardStats> {
  return api<BackofficeDashboardStats>('/api/stats/backoffice')
}

// Same bare "YYYY-MM-DD" convention as fetchStats above — fromA/toA/fromB/toB map directly to
// StatsController.Compare's query parameters, each independently optional ("no filter" for
// that side, all-time).
export function compare(
  fromA?: string,
  toA?: string,
  fromB?: string,
  toB?: string,
): Promise<CrmStatsComparison> {
  const params = new URLSearchParams()
  if (fromA) params.set('fromA', fromA)
  if (toA) params.set('toA', toA)
  if (fromB) params.set('fromB', fromB)
  if (toB) params.set('toB', toB)
  const qs = params.toString()
  return api<CrmStatsComparison>(`/api/stats/compare${qs ? `?${qs}` : ''}`)
}

// ─── Serie e ripartizioni per i grafici (Grafici G1/G3) ───────────────────────
// Date ISO yyyy-mm-dd, periodi a calendario Europe/Rome. Stesso perimetro di GET /api/stats: gli
// importi sono `null` (non zero) per chi non ha CanSeeRealAmounts.

export type SeriesGroupBy = 'day' | 'week' | 'month'

export interface StatsSeriesPoint {
  start: string
  end: string
  contracts: number
  activated: number
  expectedValue: number | null
  totalValue: number | null
  collectedValue: number | null
  leads: number
}

export interface StatsSeries {
  groupBy: SeriesGroupBy
  points: StatsSeriesPoint[]
}

export interface StatsBranch {
  branchId: number
  name: string
  contracts: number
  activated: number
  totalValue: number | null
}

export interface LeadChannelStat {
  channel: string
  label: string
  leads: number
  converted: number
}

function rangeQuery(from: string, to: string, groupBy?: SeriesGroupBy): string {
  const p = new URLSearchParams({ from, to })
  if (groupBy) p.set('groupBy', groupBy)
  return p.toString()
}

/** `GET /api/stats/series` — senza `groupBy` lo sceglie il server dall'ampiezza dell'intervallo. */
export function fetchStatsSeries(from: string, to: string, groupBy?: SeriesGroupBy): Promise<StatsSeries> {
  return api<StatsSeries>(`/api/stats/series?${rangeQuery(from, to, groupBy)}`)
}

/** `GET /api/stats/by-branch` — contratti per ramo (reparto). */
export function fetchStatsByBranch(from: string, to: string): Promise<{ branches: StatsBranch[] }> {
  return api<{ branches: StatsBranch[] }>(`/api/stats/by-branch?${rangeQuery(from, to)}`)
}

/** `GET /api/stats/leads-by-channel` — lead e convertiti per canale (fonte). */
export function fetchLeadsByChannel(from: string, to: string): Promise<{ channels: LeadChannelStat[] }> {
  return api<{ channels: LeadChannelStat[] }>(`/api/stats/leads-by-channel?${rangeQuery(from, to)}`)
}
